import { useRef } from "react";

/**
 * Hook to track API calls and ensure only the latest call's response is processed.
 *
 * @returns {Object} An object with methods to increment call ID and check if it's the latest call.
 *
 * @example
 * const { incrementCallId, isOnLastCall } = useTrackApiCalls();
 *
 * const fetchData = async () => {
 *   const callId = incrementCallId(); // Track this API call
 *   const response = await fetch('/api/data');
 *   const data = await response.json();
 *   if (isOnLastCall(callId)) {
 *     setState(data); // Only update state if this is the latest call
 *   }
 * };
 */
export const useTrackApiCalls = () => {
  const callIdRef = useRef(0); // To track the most recent call

  /**
   * Increment the call ID and return the new ID.
   * This is used to uniquely identify each API call.
   */
  const incrementCallId = () => {
    callIdRef.current += 1;
    return callIdRef.current;
  };

  /**
   * Check if the given call ID is the last one.
   * This ensures that only the latest API call's response is processed.
   */
  const isLastCall = (callId) => callIdRef.current === callId;

  return { incrementCallId, isLastCall };
};
